// src/components/PricingTable.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { STRIPE_PRODUCTS } from '../config/stripeProducts';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { getFirestore, doc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { CheckCircle2, X, Loader2 } from 'lucide-react';

// --- CONFIGURATION ---
const COMMON_FEATURES = [
  "Full CRM & Lead Management",
  "Unlimited Estimates & Invoices",
  "Job Scheduling & Dispatch",
  "Staff Time Tracking & HR",
  "Expense & Profit Reporting",
  "Email & Calendar Sync",
  "QuickBooks Integration"
];

const PLAN_HEADLINES = {
  solo:    "For Owner-Operators",
  starter: "For Small Crews",
  growth:  "For Growing Teams",
  scale:   "For Established Business"
};

// ---------------------------------------------------------------------------
// GEO-DETECTION
// Determines whether the visitor is in Canada using the browser's timezone.
// No external API needed — the Intl API is available in all modern browsers.
// Falls back to 'US' (USD) on any error so checkout never breaks.
// ---------------------------------------------------------------------------
const CANADIAN_TIMEZONES = new Set([
  'America/Toronto',       'America/Vancouver',    'America/Edmonton',
  'America/Winnipeg',      'America/Halifax',      'America/St_Johns',
  'America/Regina',        'America/Whitehorse',   'America/Yellowknife',
  'America/Dawson_Creek',  'America/Creston',      'America/Fort_Nelson',
  'America/Atikokan',      'America/Nipigon',      'America/Rainy_River',
  'America/Thunder_Bay',   'America/Moncton',      'America/Glace_Bay',
  'America/Goose_Bay',     'America/Blanc-Sablon', 'America/Iqaluit',
  'America/Rankin_Inlet',  'America/Resolute',     'America/Cambridge_Bay',
  'America/Inuvik',        'America/Dawson',       'America/Pangnirtung',
]);

const detectCountry = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return CANADIAN_TIMEZONES.has(timezone) ? 'CA' : 'US';
  } catch {
    return 'US'; // Safe fallback — USD checkout never breaks
  }
};

// a1b2c3d4 — capture the org's timezone at signup from the browser (IANA string).
// Mirrors detectCountry's pattern. Falls back to America/Toronto on empty/throw
// so signup never breaks. This is the only place a new org gets a timezone.
const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto';
  } catch {
    return 'America/Toronto';
  }
};

// ---------------------------------------------------------------------------
// AUTH MODAL
// ---------------------------------------------------------------------------
const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const [isLogin, setIsLogin]               = useState(false);
  const [email, setEmail]                   = useState('');
  const [password, setPassword]             = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName]                     = useState('');
  const [error, setError]                   = useState('');
  const [loading, setLoading]               = useState(false);
  const [termsAccepted, setTermsAccepted]   = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!isLogin && password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    if (!isLogin && !termsAccepted) {
      setError("You must agree to the Terms of Use and Privacy Policy to create an account.");
      setLoading(false);
      return;
    }

    const auth = getAuth();
    const db   = getFirestore();

    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        if (name) {
          await updateProfile(user, { displayName: name });
        }

        // Create the organization record
        const orgRef = await addDoc(collection(db, 'organizations'), {
          companyName:       `${name}'s Company`,
          createdAt:         serverTimestamp(),
          ownerUid:          user.uid,
          paymentTerms:      30,
          nextInvoiceNumber: "0001",
          nextJobNumber:     "0001",
          nextQuoteNumber:   "0001", // qnumseed-7c3b9e05 — Seam 2A: estimate/quote counter, mirrors invoice/job seeds

          timezone:          detectTimezone(), // a1b2c3d4
          quickbooksSyncTiming: 'on_send', // qbsync-8f3d2b96 -- Seam A: new orgs default to sync-on-send. Inert until QuickBooks is connected AND accounting accounts are configured; legacy orgs (no field) stay manual.
        });

        // Create the user record — include terms consent with timestamp
        await setDoc(doc(db, 'users', user.uid), {
          name:               name,
          email:              email,
          role:               "Owner/System Admin",
          organizationId:     orgRef.id,
          createdAt:          serverTimestamp(),
          status:             "active",
          subscriptionStatus: 'trialing',
          planTier:           'solo', // Updated by Stripe webhook after checkout
          termsAcceptedAt:    serverTimestamp(),
          termsVersion:       '2026-03-17', // Update this string each time you publish new terms
        });
      }

      onSuccess(); // Triggers checkout
    } catch (err) {
      console.error(err);
      setError(err.message.replace('Firebase: ', ''));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="min-h-screen px-4 text-center">
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
        <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>

        <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-slate-900 border border-white/10 shadow-2xl rounded-2xl">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-white">
              {isLogin ? 'Welcome Back' : 'Create Your Account'}
            </h3>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X size={24} />
            </button>
          </div>

          {/* Free trial callout */}
          {!isLogin && (
            <div className="mb-5 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 text-sm font-semibold text-center">
                ✓ 30-day free trial &nbsp;·&nbsp; No credit card required
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 text-red-200 text-sm rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Full Name</label>
                <input
                  type="text"
                  required
                  className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Email</label>
              <input
                type="email"
                required
                className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">Password</label>
              <input
                type="password"
                required
                className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Confirm Password</label>
                <input
                  type="password"
                  required
                  className="w-full bg-slate-800 border border-white/10 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            )}

            {/* Terms & Privacy consent — required for signup */}
            {!isLogin && (
              <div className="flex items-start gap-3 pt-1">
                <input
                  type="checkbox"
                  id="terms-consent"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-800 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
                />
                <label
                  htmlFor="terms-consent"
                  className="text-sm text-slate-400 cursor-pointer leading-relaxed"
                >
                  I agree to Spatchy's{' '}
                  <a
                    href="https://spatchy.co/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://spatchy.co/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (!isLogin && !termsAccepted)}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg shadow-lg shadow-blue-900/50 transition-all flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="animate-spin" size={18} />}
              {loading
                ? 'Processing...'
                : isLogin
                  ? 'Log In & Continue'
                  : 'Create Account & Continue'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              className="text-sm text-blue-400 hover:text-blue-300 font-medium"
              onClick={() => {
                setIsLogin(!isLogin);
                setTermsAccepted(false);
                setError('');
              }}
            >
              {isLogin ? 'Need an account? Sign Up' : 'Already have an account? Log In'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// MAIN PRICING TABLE COMPONENT
// ---------------------------------------------------------------------------
export default function PricingTable() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading]           = useState(false);
  const [isModalOpen, setIsModalOpen]   = useState(false);
  const [pendingPriceId, setPendingPriceId] = useState(null);

  // Detect country once on mount — no flicker, no API call
  const [country, setCountry] = useState('US');
  useEffect(() => {
    setCountry(detectCountry());
  }, []);

  const isCAD = country === 'CA';
  const currencyLabel = isCAD ? 'CAD' : 'USD';

  // Select the right price ID based on country and billing cycle
  const getPriceId = (product) => {
    if (isCAD) {
      return billingCycle === 'monthly'
        ? product.prices.monthly_cad
        : product.prices.annual_cad;
    }
    return billingCycle === 'monthly'
      ? product.prices.monthly
      : product.prices.annual;
  };

  // Select the right display price based on country and billing cycle
  const getDisplayPrice = (product) => {
    if (isCAD) {
      return billingCycle === 'monthly'
        ? product.displayPrices.monthly_cad
        : product.displayPrices.annual_cad;
    }
    return billingCycle === 'monthly'
      ? product.displayPrices.monthly_usd
      : product.displayPrices.annual_usd;
  };

  const handleStartClick = (priceId) => {
    const auth = getAuth();
    if (auth.currentUser) {
      handleCheckout(priceId);
    } else {
      setPendingPriceId(priceId);
      setIsModalOpen(true);
    }
  };

  const handleAuthSuccess = () => {
    setIsModalOpen(false);
    if (pendingPriceId) {
      handleCheckout(pendingPriceId);
    }
  };

  const handleCheckout = async (priceId) => {
    setLoading(true);
    try {
      const functions = getFunctions();
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const { data } = await createCheckoutSession({
        priceId,
        successUrl: 'https://app.spatchy.co/home',
        cancelUrl:  window.location.origin + '/pricing',
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Checkout failed:", error);
      alert("Something went wrong initializing checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-900 min-h-screen text-white pt-24 pb-32 font-sans">
      <Helmet>
        <title>Pricing Plans | Spatchy - Affordable Management for Small Business</title>
        <meta
          name="description"
          content="Simple, transparent pricing for owner-operators and growing teams. Every plan includes the complete Spatchy feature suite. Start your 30-day free trial — no credit card required."
        />
      </Helmet>

      <AuthModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleAuthSuccess}
      />

      <div className="max-w-7xl mx-auto px-6">

        {/* HEADER */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Every plan includes our{' '}
            <span className="text-blue-400 font-bold">Complete Feature Suite</span>.
            Simply choose the plan that fits your team size.
          </p>

          {/* CAD notice — only shown to Canadian visitors */}
          {isCAD && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-red-900/30 border border-red-700/50 rounded-full text-sm text-red-300 font-medium">
              🍁 Prices shown in Canadian dollars (CAD)
            </div>
          )}

          {/* Billing cycle toggle */}
          <div className="flex justify-center mt-8">
            <div className="bg-slate-800 p-1 rounded-lg inline-flex border border-white/10">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 text-sm font-bold rounded-md transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-2 text-sm font-bold rounded-md transition-all ${
                  billingCycle === 'annual'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Yearly <span className="text-xs ml-1 text-green-400 font-extrabold">-20%</span>
              </button>
            </div>
          </div>
        </div>

        {/* PRICING GRID */}
        <div className="grid gap-8 lg:grid-cols-4 sm:grid-cols-2 items-start">
          {Object.entries(STRIPE_PRODUCTS).map(([key, product]) => {
            const priceId      = getPriceId(product);
            const displayPrice = getDisplayPrice(product);
            const isPopular    = key === 'starter';

            return (
              <div
                key={key}
                className={`bg-slate-800/50 rounded-2xl border ${
                  isPopular
                    ? 'border-blue-500 ring-2 ring-blue-500/20'
                    : 'border-white/10'
                } flex flex-col h-full hover:bg-slate-800 transition-colors relative overflow-hidden`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}

                <div className="p-8 flex-grow">
                  <h3 className="text-xl font-bold text-white">{product.name}</h3>
                  <p className="text-sm text-blue-400 font-medium uppercase tracking-wide mt-1">
                    {PLAN_HEADLINES[key]}
                  </p>

                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">
                      ${billingCycle === 'annual'
                          ? Math.round(displayPrice / 12)
                          : displayPrice}
                    </span>
                    <span className="text-slate-400">/mo</span>
                    <span className="text-slate-500 text-xs ml-1">{currencyLabel}</span>
                  </div>

                  {billingCycle === 'annual' && (
                    <p className="text-xs text-slate-500 mt-1">
                      ${displayPrice.toFixed(2)} {currencyLabel} billed annually
                    </p>
                  )}

                  {/* Seat limit */}
                  <div className="mt-6 p-3 bg-slate-900/50 rounded-lg text-center border border-white/5">
                    <p className="text-sm font-bold text-slate-300">
                      {product.userLimit === 1
                        ? "1 Full-Access User"
                        : `Up to ${product.userLimit} Users`}
                    </p>
                  </div>

                  <button
                    onClick={() => handleStartClick(priceId)}
                    disabled={loading}
                    className={`mt-8 block w-full py-3 rounded-lg text-sm font-bold transition-all shadow-lg ${
                      isPopular
                        ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/20'
                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
                    }`}
                  >
                    {loading ? 'Loading...' : 'Start Free Trial'}
                  </button>

                  <p className="text-center text-xs text-slate-500 mt-2">
                    No credit card required
                  </p>
                </div>

                {/* Features list */}
                <div className="p-8 pt-0 flex-grow">
                  <div className="h-px bg-white/10 mb-6" />
                  <h4 className="text-xs font-bold text-slate-500 uppercase mb-4">
                    Included Features
                  </h4>
                  <ul className="space-y-3">
                    {COMMON_FEATURES.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="text-green-500 flex-shrink-0" size={18} />
                        <span className="text-sm text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom reassurance */}
        <div className="mt-16 text-center text-slate-500 text-sm space-y-1">
          <p>All plans include a 30-day free trial. No credit card required to start.</p>
          <p>Cancel anytime. Prices in {isCAD ? 'Canadian' : 'US'} dollars.</p>
          <p className="mt-4">
            <a href="/compare/spatchy-vs-jobber" className="text-blue-400 hover:text-blue-300 hover:underline transition-colors">
              See how Spatchy compares to Jobber →
            </a>
          </p>
        </div>

      </div>
    </div>
  );
}